import { useEffect, useRef } from 'react'
import { portfolioItems, mockImages } from '../../data/constants'
import { useLanguage } from '../../contexts/LanguageContext'
import styles from './Portfolio.module.css'

interface PortfolioProps {
  onPreviewOpen: (url: string, title: string) => void
}

const Portfolio = ({ onPreviewOpen }: PortfolioProps) => {
  const { t } = useLanguage()
  const portfolioRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !entry.target.classList.contains(styles.animated)) {
          entry.target.classList.add(styles.animated)
          
          const cards = entry.target.querySelectorAll(`.${styles.portfolioItem}`)
          cards.forEach((card, index) => {
            setTimeout(() => {
              card.classList.add(styles.animate)
            }, index * 100)
          })
        }
      })
    }, observerOptions)

    if (portfolioRef.current) {
      observer.observe(portfolioRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const getDomain = (url: string): string => {
    try {
      return new URL(url).hostname.replace('www.', '')
    } catch {
      return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
    }
  }

  const getImageUrl = (item: typeof portfolioItems[0]): string => {
    const domain = item.domain || getDomain(item.url)
    return mockImages[domain] || '#000000'
  }

  // Farklı boyutlar için grid pattern
  const getItemSize = (index: number): string => {
    const patterns = ['normal', 'wide', 'tall', 'normal', 'tall', 'wide', 'normal', 'normal', 'wide', 'tall']
    return patterns[index % patterns.length]
  }

  return (
    <section id="portfolio" className={styles.portfolio}>
      <div className={styles.container}>
        <div className={styles.portfolioHeader}>
          <p className={styles.pill}>{t('portfolio.pill')}</p>
          <h2 className={styles.sectionTitle}>{t('portfolio.title')}</h2>
          <p className={styles.sectionSubtitle}>
            {t('portfolio.subtitle')}
          </p>
        </div>
        <div className={styles.portfolioGrid} ref={portfolioRef}>
          {portfolioItems.map((item, index) => {
            const imageUrl = getImageUrl(item)
            const size = getItemSize(index)
            return (
              <div
                key={index}
                className={`${styles.portfolioItem} ${styles[size]}`}
                onClick={() => onPreviewOpen(item.url, item.title)}
              >
                <div className={styles.portfolioImage}>
                  <div
                    className={styles.websitePreview}
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }}
                  ></div>
                  <div className={styles.portfolioOverlay}>
                    <div className={styles.portfolioContent}>
                      <h3 className={styles.portfolioTitle}>{item.title}</h3>
                      <div className={styles.portfolioDomain}>
                        {getDomain(item.url)}
                      </div>
                      <button
                        className={styles.previewBtn}
                        onClick={(e) => {
                          e.stopPropagation()
                          onPreviewOpen(item.url, item.title)
                        }}
                      >
                        <span>{t('portfolio.preview')}</span>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className={styles.portfolioGradient}></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Portfolio
